/**
 * Which metrics window a flow tag rule is evaluated against.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The optimizer cube is a `Map<cubeKey, metricsByItem>`; every preview item
 * carries the same keys in `metricsByDateRange`. A tag rule declares its own
 * window, so it needs the SAME key the cube builder registered — otherwise the
 * evaluator's lookup misses and `pickMetrics` silently falls through to some
 * other window's numbers (a "Last 7 days" rule judged on 30-day stats).
 *
 * Preset windows deliberately key by the bare preset so a rule shares the cube
 * entry — and therefore the report call — with any formula group or exclusion
 * group on the same preset. Custom windows key by rule id: two rules can hold
 * different explicit bounds, and the literal "custom" would collide.
 */

import type { FlowTagRule } from "./types";

export type FlowTagRuleWindow = Pick<
  FlowTagRule,
  "id" | "dateRangePreset" | "fromDate" | "toDate" | "toDateIsToday"
>;

/**
 * `undefined` when the rule declares no preset at all — callers then keep the
 * legacy "use the only/first window" behaviour instead of looking up a key
 * that was never registered.
 */
export function flowTagRuleCubeKey(
  rule: FlowTagRuleWindow,
): string | undefined {
  const preset = (rule.dateRangePreset ?? "").trim();
  if (!preset) return undefined;
  if (preset !== "custom") return preset;
  return `custom:tag:${rule.id}`;
}

/** True when the rule's window is complete enough to fetch a report for. */
export function flowTagRuleHasWindow(rule: FlowTagRuleWindow): boolean {
  const preset = (rule.dateRangePreset ?? "").trim();
  if (!preset) return false;
  if (preset !== "custom") return true;
  if (!rule.fromDate?.trim()) return false;
  return rule.toDateIsToday === true || Boolean(rule.toDate?.trim());
}
